import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar as CalendarIcon, 
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  FileText,
  Target,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, parseISO } from 'date-fns';

const EVENT_COLORS = {
  task: '#6366f1',       // indigo
  milestone: '#10b981',  // emerald
  deadline: '#ef4444',   // red
  meeting: '#8b5cf6',    // violet
};

const EVENT_ICONS = {
  task: FileText,
  milestone: CheckCircle2,
  deadline: AlertCircle,
  meeting: Users,
};

const DayCell = ({ date, events, isCurrentMonth, onClick }) => {
  const dayEvents = events.filter(e => isSameDay(parseISO(e.start_date), date));
  const isCurrentDay = isToday(date);
  
  return (
    <div 
      className={`min-h-[100px] p-2 border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
        !isCurrentMonth ? 'bg-slate-50 dark:bg-slate-900 opacity-50' : ''
      } ${isCurrentDay ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
      onClick={() => onClick(date)}
    >
      <div className={`text-sm font-medium mb-1 ${
        isCurrentDay ? 'w-7 h-7 rounded-full bg-indigo-500 text-white flex items-center justify-center' : ''
      }`}>
        {format(date, 'd')}
      </div>
      <div className="space-y-1">
        {dayEvents.slice(0, 3).map((event) => {
          const EventIcon = EVENT_ICONS[event.event_type] || FileText;
          return (
            <div
              key={event.event_id}
              className="text-xs px-1.5 py-0.5 rounded truncate flex items-center gap-1"
              style={{ backgroundColor: `${event.color || EVENT_COLORS[event.event_type] || '#6366f1'}20` }}
            >
              <EventIcon className="w-3 h-3 flex-shrink-0" style={{ color: event.color || EVENT_COLORS[event.event_type] }} />
              <span className="truncate">{event.title}</span>
            </div>
          );
        })}
        {dayEvents.length > 3 && (
          <div className="text-xs text-slate-500">+{dayEvents.length - 3} more</div>
        )}
      </div>
    </div>
  );
};

const EventForm = ({ onSubmit, onClose, initialDate, clients }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'task',
    start_date: initialDate ? format(initialDate, "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    all_day: false,
    client_id: '',
    color: '#6366f1'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Event Title</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Enter event title"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Event details..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="event_type">Event Type</Label>
          <Select
            value={formData.event_type}
            onValueChange={(value) => setFormData({ ...formData, event_type: value, color: EVENT_COLORS[value] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="task">Task</SelectItem>
              <SelectItem value="milestone">Milestone</SelectItem>
              <SelectItem value="deadline">Deadline</SelectItem>
              <SelectItem value="meeting">Meeting</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="client">Client</Label>
          <Select
            value={formData.client_id}
            onValueChange={(value) => setFormData({ ...formData, client_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No client</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.user_id} value={client.user_id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="start_date">Date & Time</Label>
        <Input
          id="start_date"
          type="datetime-local"
          value={formData.start_date}
          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
          required
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="all_day"
          checked={formData.all_day}
          onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
          className="rounded border-slate-300"
        />
        <Label htmlFor="all_day" className="text-sm cursor-pointer">All day event</Label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" className="gds-btn-primary">Create Event</Button>
      </DialogFooter>
    </form>
  );
};

const EventDetails = ({ event, onClose, onDelete }) => {
  const EventIcon = EVENT_ICONS[event.event_type] || FileText;
  
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${event.color || EVENT_COLORS[event.event_type]}20` }}
        >
          <EventIcon 
            className="w-5 h-5" 
            style={{ color: event.color || EVENT_COLORS[event.event_type] }}
          />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{event.title}</h3>
          <Badge variant="outline" className="mt-1 capitalize">
            {event.event_type}
          </Badge>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Clock className="w-4 h-4" />
          {format(parseISO(event.start_date), 'PPP')}
          {!event.all_day && ` at ${format(parseISO(event.start_date), 'p')}`}
        </div>
        
        {event.description && (
          <p className="text-sm text-slate-600">{event.description}</p>
        )}
      </div>

      <DialogFooter>
        <Button variant="destructive" onClick={() => onDelete(event.event_id)}>
          Delete Event
        </Button>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </DialogFooter>
    </div>
  );
};

export default function CalendarPage() {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const isInternalTeam = ['admin', 'project_manager', 'content_account', 'creative_account', 'execution_team'].includes(user?.role);

  useEffect(() => {
    fetchEvents();
    fetchUpcoming();
    if (isInternalTeam) {
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const fetchEvents = async () => {
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const response = await api.get('/calendar/events', {
        params: { start_date: start, end_date: end }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async () => {
    try {
      const response = await api.get('/calendar/upcoming', { params: { days: 7 } });
      setUpcomingEvents(response.data);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/execution/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleCreateEvent = async (formData) => {
    try {
      await api.post('/calendar/events', {
        ...formData,
        client_id: formData.client_id === 'none' ? null : formData.client_id,
        start_date: new Date(formData.start_date).toISOString(),
        assigned_to: []
      });
      toast.success('Event created');
      setShowEventForm(false);
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/calendar/events/${eventId}`);
      toast.success('Event deleted');
      setSelectedEvent(null);
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleDayClick = (date) => {
    const dayEvents = events.filter(e => isSameDay(parseISO(e.start_date), date));
    
    if (dayEvents.length === 1) {
      setSelectedEvent(dayEvents[0]);
    } else if (dayEvents.length > 1) {
      // Could show a list of events for this day
      setSelectedDate(date);
      if (isInternalTeam) {
        setShowEventForm(true);
      }
    } else if (isInternalTeam) {
      setSelectedDate(date);
      setShowEventForm(true);
    }
  };

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Add days from previous month to start on Sunday
  const firstDayOfMonth = startOfMonth(currentMonth);
  const startPadding = firstDayOfMonth.getDay();
  const paddedDays = [];
  
  for (let i = startPadding - 1; i >= 0; i--) {
    const date = new Date(firstDayOfMonth);
    date.setDate(date.getDate() - i - 1);
    paddedDays.push(date);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Calendar</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Schedule and track work assignments
          </p>
        </div>
        {isInternalTeam && (
          <Button 
            className="gds-btn-primary"
            onClick={() => {
              setSelectedDate(new Date());
              setShowEventForm(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-3 gds-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-indigo-500" />
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setCurrentMonth(new Date())}
                >
                  Today
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div 
                  key={day} 
                  className="text-center text-sm font-medium text-slate-600 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7">
              {/* Padding days from previous month */}
              {paddedDays.map((date, i) => (
                <DayCell
                  key={`pad-${i}`}
                  date={date}
                  events={events}
                  isCurrentMonth={false}
                  onClick={() => {}}
                />
              ))}
              
              {/* Current month days */}
              {days.map((date) => (
                <DayCell
                  key={date.toISOString()}
                  date={date}
                  events={events}
                  isCurrentMonth={true}
                  onClick={handleDayClick}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events Sidebar */}
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Upcoming (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No upcoming events</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => {
                  const EventIcon = EVENT_ICONS[event.event_type] || FileText;
                  return (
                    <motion.div
                      key={event.event_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="flex items-start gap-2">
                        <div 
                          className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${event.color || EVENT_COLORS[event.event_type]}20` }}
                        >
                          <EventIcon 
                            className="w-4 h-4" 
                            style={{ color: event.color || EVENT_COLORS[event.event_type] }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{event.title}</p>
                          <p className="text-xs text-slate-500">
                            {format(parseISO(event.start_date), 'MMM d')}
                            {!event.all_day && ` • ${format(parseISO(event.start_date), 'p')}`}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Event Dialog */}
      <Dialog open={showEventForm} onOpenChange={setShowEventForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Event</DialogTitle>
          </DialogHeader>
          <EventForm
            onSubmit={handleCreateEvent}
            onClose={() => setShowEventForm(false)}
            initialDate={selectedDate}
            clients={clients}
          />
        </DialogContent>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Event Details</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <EventDetails
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
              onDelete={handleDeleteEvent}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
